import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { Box, Theme, alpha } from '@mui/material';
import { PointerEvent, ReactNode, Ref } from 'react';
import { isTouchEnabled } from '../../../../../../Theme';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { getGridSurfaceStyles } from '../../../../../orders/styles/gridScrollFade';
import {
  COMBINATION_GRID_GAP_WIDTH,
  COMBINATION_PRICE_SECTION_WIDTH,
} from '../../../../../products/products-details-v2/components/CombinationLine/consts/combinationLine.consts';
import { useTranslation } from '../../../../../translation/TranslationContext';
import {
  CATALOG_LIST_CHECKBOX_GAP,
  CATALOG_LIST_CHECKBOX_SLOT_WIDTH,
  CATALOG_LIST_CHIPS_GAP,
  CATALOG_LIST_CHIPS_SLOT_SX,
  CATALOG_LIST_CHIPS_TO_GRID_GAP,
  CATALOG_LIST_RIGHT_BLOCK_GAP,
  CATALOG_LIST_RIGHT_BLOCK_WIDTH,
  CATALOG_LIST_ROW_TAB_INDEX,
  CATALOG_LIST_ROW_VERTICAL_PADDING,
  CATALOG_LIST_STACKED_CHIPS_SLOT_SX,
  CATALOG_LIST_STACKED_INFO_TO_CHIPS_GAP,
  getCatalogListLeftColumnWidth,
} from '../utils/catalogListRow.consts';

export interface CatalogListRowDragProps {
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  activatorRef?: Ref<HTMLDivElement>;
}

export interface CatalogListRowAdjacentSelection {
  isPreviousSelected: boolean;
  isNextSelected: boolean;
}

interface CatalogListRowSelection extends Partial<CatalogListRowAdjacentSelection> {
  isSelected: boolean;
  isLocked: boolean;
  onToggle: () => void;
  productName: string;
}

interface CatalogProductListRowProps {
  isStacked: boolean;
  rowRef?: Ref<HTMLDivElement>;
  left: ReactNode;
  grid: ReactNode;
  price: ReactNode;
  selection?: CatalogListRowSelection;
  combinations?: ReactNode;
  addButton?: ReactNode;
  dragProps?: CatalogListRowDragProps;
}

const touchKey = isTouchEnabled ? 'touch' : 'mouse';

const SELECTED_ROW_TINT_OPACITY = 0.05;
const SELECTED_ROW_HOVER_TINT_OPACITY = 0.1;

const HOVER_CAPABLE_POINTER = '@media (hover: hover)';

const getRowSurface = (theme: Theme) => ({
  ...getGridSurfaceStyles({ base: theme.palette.background.paper }),
  [HOVER_CAPABLE_POINTER]: {
    '&:hover': getGridSurfaceStyles({ base: theme.palette.action.hover }),
  },
});

const getSelectedRowRadius = (theme: Theme, selection: CatalogListRowSelection) => {
  const radius = `${theme.shape.borderRadiusLg}px`;
  const top = selection.isPreviousSelected ? '0' : radius;
  const bottom = selection.isNextSelected ? '0' : radius;

  return `${top} ${top} ${bottom} ${bottom}`;
};

const getSelectedRowSurface = (selection: CatalogListRowSelection) => (theme: Theme) => ({
  ...getGridSurfaceStyles({
    base: theme.palette.background.paper,
    tint: alpha(theme.palette.primary.main, SELECTED_ROW_TINT_OPACITY),
  }),
  borderRadius: getSelectedRowRadius(theme, selection),
  [HOVER_CAPABLE_POINTER]: {
    '&:hover': getGridSurfaceStyles({
      base: theme.palette.background.paper,
      tint: alpha(theme.palette.primary.main, SELECTED_ROW_HOVER_TINT_OPACITY),
    }),
  },
});

const ROW_SX = {
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  overflow: 'hidden',
  paddingX: 1,
  paddingY: `${CATALOG_LIST_ROW_VERTICAL_PADDING}px`,
  borderRadius: (theme: Theme) => `${theme.shape.borderRadiusLg}px`,
} as const;

const PRICE_SLOT_SX = { width: COMBINATION_PRICE_SECTION_WIDTH, flex: '0 0 auto' } as const;

const GRID_SLOT_SX = { minWidth: 0, '& input': { cursor: 'auto' } } as const;

const stopDragActivationFromInput = (event: PointerEvent<HTMLDivElement>) => {
  if (!(event.target as Element).closest('input')) {
    return;
  }

  event.stopPropagation();
};

const CHIPS_BAND_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: `${CATALOG_LIST_CHIPS_GAP}px`,
  minWidth: 0,
} as const;

const CatalogProductListRow = ({
  isStacked,
  rowRef,
  left,
  grid,
  price,
  selection,
  combinations,
  addButton,
  dragProps,
}: CatalogProductListRowProps) => {
  const { translate } = useTranslation();

  const checkboxSlot = selection && (
    <Box sx={{ width: CATALOG_LIST_CHECKBOX_SLOT_WIDTH, flex: '0 0 auto', alignSelf: 'center' }}>
      <Checkbox
        size="small"
        checked={selection.isSelected}
        disabled={selection.isLocked}
        onChange={selection.onToggle}
        onClick={(event) => event.stopPropagation()}
        slotProps={{
          input: {
            tabIndex: CATALOG_LIST_ROW_TAB_INDEX,
            'test-id': CatalogProductsTestIds.PRODUCT_CHECKBOX,
            'aria-label': translate(LangEnum.CATALOGS_MASS_ACTIONS_SELECT_PRODUCT, {
              name: selection.productName,
            }),
          },
        }}
      />
    </Box>
  );

  const rowCursor = dragProps?.listeners ? 'grab' : undefined;

  const selectedSurface = selection?.isSelected ? getSelectedRowSurface(selection) : false;

  const activatorBlock = (
    <Box
      ref={dragProps?.activatorRef}
      test-id={CatalogProductsTestIds.PRODUCT_ROW_DRAG_HANDLE}
      sx={{ minWidth: 0, flex: '1 1 auto' }}
      {...dragProps?.attributes}
      tabIndex={CATALOG_LIST_ROW_TAB_INDEX}
    >
      {left}
    </Box>
  );

  if (isStacked) {
    return (
      <Box
        ref={rowRef}
        sx={[
          ROW_SX,
          getRowSurface,
          selectedSurface,
          { flexDirection: 'column', cursor: rowCursor },
        ]}
        {...dragProps?.listeners}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: `${CATALOG_LIST_CHECKBOX_GAP[touchKey]}px`,
          }}
        >
          {checkboxSlot}
          {activatorBlock}
          <Box test-id={CatalogProductsTestIds.PRODUCT_PRICE} sx={PRICE_SLOT_SX}>
            {price}
          </Box>
        </Box>

        <Box sx={{ ...CHIPS_BAND_SX, marginTop: `${CATALOG_LIST_STACKED_INFO_TO_CHIPS_GAP}px` }}>
          <Box sx={CATALOG_LIST_STACKED_CHIPS_SLOT_SX}>{combinations}</Box>
        </Box>

        <Box
          sx={{ ...GRID_SLOT_SX, marginTop: `${CATALOG_LIST_CHIPS_TO_GRID_GAP}px` }}
          onPointerDown={stopDragActivationFromInput}
          onMouseDown={stopDragActivationFromInput}
        >
          {grid}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={rowRef}
      sx={[
        ROW_SX,
        getRowSurface,
        selectedSurface,
        {
          alignItems: 'flex-start',
          gap: `${COMBINATION_GRID_GAP_WIDTH}px`,
          cursor: rowCursor,
        },
      ]}
      {...dragProps?.listeners}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${CATALOG_LIST_CHECKBOX_GAP[touchKey]}px`,
          flex: '0 1 auto',
          minWidth: 0,
          width: getCatalogListLeftColumnWidth({
            isTouchEnabled,
            hasSelection: Boolean(selection),
          }),
        }}
      >
        {checkboxSlot}
        {activatorBlock}
      </Box>

      <Box
        sx={{
          flex: '1 0 0',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: `${CATALOG_LIST_CHIPS_TO_GRID_GAP}px`,
        }}
      >
        <Box sx={CHIPS_BAND_SX}>
          <Box sx={CATALOG_LIST_CHIPS_SLOT_SX}>{combinations}</Box>
        </Box>

        <Box
          sx={GRID_SLOT_SX}
          onPointerDown={stopDragActivationFromInput}
          onMouseDown={stopDragActivationFromInput}
        >
          {grid}
        </Box>
      </Box>

      <Box
        sx={{
          width: CATALOG_LIST_RIGHT_BLOCK_WIDTH,
          flex: '0 0 auto',
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          gap: `${CATALOG_LIST_RIGHT_BLOCK_GAP}px`,
        }}
      >
        <Box test-id={CatalogProductsTestIds.PRODUCT_PRICE} sx={PRICE_SLOT_SX}>
          {price}
        </Box>
        {addButton}
      </Box>
    </Box>
  );
};

export default CatalogProductListRow;
